/**
 * MY Transfer of Shares — M32201 mapper.
 *
 * Variable Map 출처: `Transfer_of_Shares_Preset_Draft.md`.
 * 3개 문서 패키지의 공통/문서별 변수를 templates 가 사용할 형식으로 변환.
 *   1. Directors' Circular Resolution (Transfer) — 이사 전원(당사자 이사 이해관계 배제), 반복부 transfers
 *   2. Section 105 (Transfer)                    — 양도인/양수인 단건 평면 + 증인 4명 + 체크박스
 *   3. Waiver of Preemption Rights (Transfer)    — 매도자 제외 기존 주주 전원, 반복부 transfers
 *
 * allotmentSharesMapper 패턴 답습:
 *   - snake_case 변수 + dot alias 양쪽 출력
 *   - detectXxxDocFlags 는 templateName 정규식으로 문서 포함 여부 감지
 *   - 날짜는 패널이 execution_date 통합 + 문서별 override + skip 을 resolve 한 최종값을 넘김(여기선 그대로 출력)
 */

/** Governing document type — DCR 의 resolution_basis_* 분기 (AOD/AGM/Allotment 와 동일). */
export type GoverningDocumentType = 'constitution' | 'articles' | 'third_schedule';

/** Section 105 증인 1행 (총 4명 — 1·2 양도인측, 3·4 양수인측). */
export interface WitnessInput {
  name?: string;
  address?: string;
  occupation?: string;
}

/**
 * Section 105 (Instrument of Transfer) — 이제 Transfer 1행마다 1부 생성.
 *   자동 매핑 필드(number_of_units=shares, certificate_no=기존증서, securities=shareType,
 *   transferor_name/transferee_name)는 TransferRow 에서 직접 파생하므로 여기엔 담지 않는다.
 *   여기엔 Section 105 고유의 수동/보조 입력만 보관.
 */
export interface Section105Data {
  /** 대가 (문자) — 수동 */
  considerationWords?: string;
  /** 대가 (RM, 숫자) — 수동 */
  considerationFigures?: string;
  /** 양도인 성명 (block letters) — lookup 시 자동, 수정 가능 */
  transferorDetails?: string;
  /** 양도인 IC/여권/회사번호 — lookup 시 자동, 수정 가능 */
  transferorId?: string;
  /** 양수인 성명 & 주소 */
  transfereeDetails?: string;
  /** (개인) IC/여권 + 국적 */
  transfereePerson?: string;
  /** (법인) 회사번호 + 설립지 */
  transfereeCorporate?: string;
  /** 양수인 분류 — 개인/법인 */
  transfereeKind?: 'individual' | 'corporate' | '';
  /** (개인) 인종 */
  transfereeRace?: 'malay' | 'chinese' | 'indian' | 'others' | '';
  /** (법인) 유형 */
  transfereeCorporateType?: 'gov' | 'mn' | 'mnn' | 'nm' | '';
  /** 증인 4명 */
  witnesses?: WitnessInput[];
}

/** 양도 내역 1행 — DCR(1)/Waiver(3) 반복부 [[if transfers]] 행 + 자체 Section 105(문서2) 1부. */
export interface TransferRow {
  /** 양도인(Transferor) 성명 */
  transferorName: string;
  /** Person Lookup 으로 선택한 양도인 contactCd — 패널에서 증서 콤보 필터용 (문서 변수 미출력) */
  transferorContactCd?: string;
  /** 양도인 등록번호/NRIC — 레지스터 write(TFOR_REG_NO)용. Person Lookup 시 캡처. */
  transferorRegNo?: string;
  /** 기존 주권 번호 */
  certificateNo?: string;
  /** 양도 주식 수 */
  shares?: number | string;
  /** 주식 종류 (Ordinary/Preference/Others) — Section 105 securities_description 파생용 */
  shareType?: string;
  /** 양수인(Transferee) 성명 */
  transfereeName: string;
  /** Person Lookup / Register 로 선택한 양수인 contactCd (문서 변수 미출력) */
  transfereeContactCd?: string;
  /** 양수인 등록번호/NRIC — 레지스터 write(TEE_REG_NO)용. Person Lookup 시 캡처. */
  transfereeRegNo?: string;
  /** 신규 주권 번호 */
  newCertificateNo?: string;
  /** 신규 주식 수 */
  newShares?: number | string;
  /** 이 Transfer 전용 Section 105 데이터 (Transfer 1건 = Section 105 1부) */
  section105?: Section105Data;
}

/** 서명 주체(이사/주주) raw — backend register / signing 호환용. */
export interface SignerInput {
  contactCd?: string;
  name: string;
  email?: string;
  nricNo?: string;
  identificationNo?: string;
}

export interface TransferSharesInput {
  // ── 회사 ──
  companyName: string;
  companyRegistrationNo: string;
  /** 구 등록번호 — [[old_registration_no]] 치환용 (없으면 backend prepare-documents 가 entity 에서 보충). */
  oldRegistrationNo?: string;
  constitutionClauseNo?: string;
  constitutionClauseNumber?: string;
  governingDocumentType?: GoverningDocumentType; // 기본 'constitution'
  hasConstitution?: boolean;

  // ── 구성 분기 ──
  directorCount?: 'single' | 'multiple'; // is_sole_director_company / is_multiple_director_company
  memberCount?: 'single' | 'multiple';   // is_sole_member / is_multiple_member (Waiver)
  hasCommonSeal?: boolean;                // has_common_seal / no_common_seal

  // ── 양도 내역 (반복부, 문서 1·3) ──
  transfers: TransferRow[];
  totalSharesTransferred?: number | string;

  // ── 날짜 (패널이 execution_date 통합 + override + skip resolve 후 최종값 전달) ──
  executionDate?: string;
  resolutionDate?: string; // 문서1 DCR
  transferDate?: string;   // 문서2 Section 105
  letterDate?: string;     // 문서3 Waiver

  // ── Section 105 은 이제 Transfer 행마다 개별 문서 → buildSection105Vars 로 별도 생성 ──

  // ── 서명 주체 raw ──
  /** 문서1 DCR — 이사 전원(당사자 이사 제외 후 잔여) */
  directors?: SignerInput[];
  /** 문서3 Waiver — 매도자(양도인) 제외 기존 주주 전원 */
  waiverMembers?: SignerInput[];

  // ── 기타 ──
  submissionReference?: string;
}

const norm = (v: any) => String(v ?? '').trim();

/** 동일 단위의 반복 문자열(X, XX...)이면 단위 1개로 축약 — consideration_words 중복 바인딩 방지 안전장치. */
function collapseSelfRepeat(str: string): string {
  const s = String(str || '').trim();
  const n = s.length;
  if (n < 2) return s;
  for (let len = 1; len <= n / 2; len++) {
    if (n % len !== 0) continue;
    const unit = s.slice(0, len);
    if (unit.repeat(n / len) === s) return unit;
  }
  return s;
}

/**
 * "이름, 주소..." 한 줄 → 문서용 줄바꿈 블록.
 *   LEO YEO DEV, No. 18, Jalan... → LEO YEO DEV\nNo. 18, Jalan...
 */
export function formatNameAddressForDocument(value: string, fallbackName?: string): string {
  const raw = norm(value) || norm(fallbackName);
  if (!raw) return '';
  if (raw.includes('\n')) return raw;
  const commaIdx = raw.indexOf(', ');
  if (commaIdx <= 0) return raw;
  const head = raw.slice(0, commaIdx).trim();
  const tail = raw.slice(commaIdx + 2).trim();
  if (!tail) return head;
  // 이름 뒤에 주소가 콤마로 붙은 패턴 — 주소는 숫자/No./Jalan 등으로 시작하는 경우가 많음
  if (!/^\d|no\.|lot|jalan|jl\.|blk|block|unit|#/i.test(head)) {
    return `${head}\n${tail}`;
  }
  return raw;
}

/** 3개 문서 패키지 — 각 문서 포함 여부 + 분기 (SKILL.md 표준 detectXxxDocFlags 패턴). */
export interface TransferSharesDocFlags {
  /** 1. Directors' Circular Resolution (Transfer) */
  hasDcr: boolean;
  /** 2. Section 105 (Transfer) */
  hasSection105: boolean;
  /** 3. Waiver of Preemption Rights (Transfer) — 조건부(정관 선매권) */
  hasWaiver: boolean;
  /** 양도 내역 반복 블록 섹션 노출 — 1·3 중 하나라도 있으면 */
  hasAnyTransfers: boolean;
}

const any = (re: RegExp, docs: Array<{ templateName?: string; docCode?: string }>) =>
  docs.some((d) => re.test(String(d?.templateName || '')));

export function detectTransferSharesDocFlags(
  documents: Array<{ templateName?: string; docCode?: string }>,
): TransferSharesDocFlags {
  const docs = documents || [];
  const hasDcr = any(/director.*circular|circular.*resolution|\bdcr\b/i, docs);
  const hasSection105 = any(/section\s*105|form\s*of\s*transfer|transfer\s*of\s*securities/i, docs);
  const hasWaiver = any(/waiver.*pre-?emption|pre-?emption.*rights/i, docs);
  return {
    hasDcr,
    hasSection105,
    hasWaiver,
    hasAnyTransfers: hasDcr || hasWaiver,
  };
}

/** 체크박스 셀 마커 — 선택된 항목은 'X', 나머지는 ''. [[r_my]] 직접치환 / [[if r_my]] 둘 다 호환. */
const mark = (on: boolean) => (on ? 'X' : '');

/** 양도 내역 반복부 row builder.
 *   ⚠ documentVariableReplacer 의 [[if transfers]] 반복부는 item 객체의 "키 = 플레이스홀더 전체 이름" 으로
 *     [[key]] 를 치환한다 (prefix 자동 부여 없음). row 키는 템플릿 변수명과 정확히 일치해야 한다.
 *   underscore(신) + dot(구 변수맵) 양쪽 키 모두 출력.
 */
function buildTransferRow(t: TransferRow, idx: number): Record<string, string> {
  const no = String(idx + 1);
  const transferorName = norm(t.transferorName);
  const certificateNo = norm(t.certificateNo);
  const shares = norm(t.shares);
  const transfereeName = norm(t.transfereeName);
  const newCertificateNo = norm(t.newCertificateNo);
  const newShares = norm(t.newShares);
  return {
    transfer_no: no, 'transfer.no': no,
    transfer_transferor_name: transferorName, 'transfer.transferor_name': transferorName,
    transfer_certificate_no: certificateNo, 'transfer.certificate_no': certificateNo,
    transfer_shares: shares, 'transfer.shares': shares,
    transfer_transferee_name: transfereeName, 'transfer.transferee_name': transfereeName,
    transfer_new_certificate_no: newCertificateNo, 'transfer.new_certificate_no': newCertificateNo,
    transfer_new_shares: newShares, 'transfer.new_shares': newShares,
  };
}

/**
 * 3개 문서가 사용할 변수 집합을 단일 record 로 변환.
 */
export function buildTransferSharesVariables(input: TransferSharesInput): Record<string, any> {
  const transfers = (input.transfers || []).filter((t) => norm(t.transferorName) || norm(t.transfereeName));
  const transferRows = transfers.map((t, idx) => buildTransferRow(t, idx));

  // 양도 주식 총수 — 명시값 없으면 행 합산.
  const totalShares = input.totalSharesTransferred != null && norm(input.totalSharesTransferred) !== ''
    ? norm(input.totalSharesTransferred)
    : String(transfers.reduce((sum, t) => sum + (Number(t.shares || 0) || 0), 0));

  // ── 거버닝-문서 분기 (Allotment/AOD 동일) ──
  const govType: GoverningDocumentType = input.governingDocumentType || 'constitution';
  const stripClause = (v: string) => {
    const s = norm(v);
    if (!s) return '';
    const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
    if (m1) return m1[1];
    const m2 = s.match(/\b(\d+[\w()]*)/);
    return m2 ? m2[1] : s;
  };
  const constitutionClauseNumber = govType === 'third_schedule'
    ? ''
    : (stripClause(input.constitutionClauseNumber || '') || stripClause(input.constitutionClauseNo || ''));
  const isSingleDirector = input.directorCount === 'single';
  const isSingleMember = input.memberCount === 'single';
  const resolutionBasisClause =
    govType === 'third_schedule'
      ? 'Paragraph 15 of the Third Schedule of the Companies Act 2016'
      : govType === 'articles'
        ? (constitutionClauseNumber
            ? `Article ${constitutionClauseNumber} of the Company's Constitution (Articles of Association)`
            : "the Company's Constitution (Articles of Association)")
        : (constitutionClauseNumber
            ? `Clause ${constitutionClauseNumber} of the Company's Constitution`
            : "the Company's Constitution");

  return {
    // === 회사 ===
    company_name: input.companyName,
    company_registration_no: input.companyRegistrationNo,
    old_registration_no: norm(input.oldRegistrationNo),
    constitution_clause_no: constitutionClauseNumber || (input.constitutionClauseNo || ''),
    has_constitution: govType !== 'third_schedule' ? 'true' : '',

    // === 거버닝-문서 분기 (문서 1·3 공통) ===
    constitution_clause_number: constitutionClauseNumber,
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',
    resolution_basis_clause: resolutionBasisClause,
    governing_document_clause: resolutionBasisClause,
    // 구버전 alias
    constitution: govType === 'constitution',
    articles: govType === 'articles',
    third_schedule: govType === 'third_schedule',
    // [[if is_sole_director_company]] / [[if is_multiple_director_company]] (DCR)
    is_sole_director_company: isSingleDirector,
    is_multiple_director_company: !isSingleDirector,
    // [[if is_sole_member]] / [[if is_multiple_member]] (Waiver)
    is_sole_member: isSingleMember,
    is_multiple_member: !isSingleMember,
    // [[if has_common_seal]] / [[if no_common_seal]] (DCR 주권 발행 방식)
    has_common_seal: !!input.hasCommonSeal,
    no_common_seal: !input.hasCommonSeal,

    // === 양도 내역 (반복 블록, 문서 1·3) ===
    //   [[if transfers]] / [[if transfer]] / [[/if transfers]] / [[/if transfer]] 모두 대응 (allottee 패턴).
    transfers: transferRows,
    transfer: transferRows,
    _repeat_transfers: transferRows,
    _repeat_transfer: transferRows,
    total_shares_transferred: totalShares,

    // === 날짜 (패널 resolve 결과 그대로 — execution_date 통합/override/skip) ===
    execution_date: input.executionDate || '',
    resolution_date: input.resolutionDate || '',
    transfer_date: input.transferDate || '',
    letter_date: input.letterDate || '',

    // === 기타 ===
    submission_reference: input.submissionReference || '',

    // === Raw 정보 (backend register / signing 호환 보존) ===
    __directorsRaw: (input.directors || []).map((d) => ({
      contactCd: norm(d.contactCd),
      name: norm(d.name),
      email: norm(d.email),
      nricNo: norm(d.nricNo) || norm(d.identificationNo),
    })),
    __waiverMembersRaw: (input.waiverMembers || []).map((m) => ({
      contactCd: norm(m.contactCd),
      name: norm(m.name),
      email: norm(m.email),
      nricNo: norm(m.nricNo) || norm(m.identificationNo),
    })),
    __transfersRaw: transfers.map((t) => ({
      transferorName: norm(t.transferorName),
      certificateNo: norm(t.certificateNo),
      shares: Number(t.shares || 0) || 0,
      shareType: norm(t.shareType) || 'Ordinary',
      transfereeName: norm(t.transfereeName),
      newCertificateNo: norm(t.newCertificateNo),
      newShares: Number(t.newShares || 0) || 0,
    })),
  };
}

/** 주식 종류 → Section 105 securities_description ("Ordinary" → "Ordinary Shares"). */
export function securitiesDescriptionFromShareType(shareType?: string): string {
  const s = norm(shareType) || 'Ordinary';
  return /shares?$/i.test(s) ? s : `${s} Shares`;
}

/**
 * Section 105 (Instrument of Transfer) 변수 — Transfer 1행 기준 1부.
 *   자동 매핑: number_of_units=shares, certificate_no=기존증서, securities_description=shareType,
 *             transferor_name/transferee_name=행 값.
 *   수동/보조: consideration_*, transferor_details/id, transferee_details/person/corporate + 분류, witnesses.
 *   회사/날짜 등 공통 변수는 buildTransferSharesVariables 결과에 merge 해서 사용.
 */
export function buildSection105Vars(
  row: TransferRow,
  common: { transferDate?: string } = {},
): Record<string, any> {
  const s = row.section105 || {};
  const kind = s.transfereeKind || '';
  const race = s.transfereeRace || '';
  const corpType = s.transfereeCorporateType || '';

  const w = s.witnesses || [];
  const witnessVars: Record<string, string> = {};
  for (let i = 0; i < 4; i++) {
    const n = String(i + 1);
    witnessVars[`witness_${n}_name`] = norm(w[i]?.name);
    witnessVars[`witness_${n}_address`] = norm(w[i]?.address);
    witnessVars[`witness_${n}_occupation`] = norm(w[i]?.occupation);
  }

  const transferorName = norm(row.transferorName);
  const transfereeName = norm(row.transfereeName);

  return {
    // 자동 매핑 (Transfer Information → Section 105)
    number_of_units: norm(row.shares),
    securities_description: securitiesDescriptionFromShareType(row.shareType),
    certificate_no: norm(row.certificateNo),
    transfer_date: common.transferDate || '',
    // 대가 (수동)
    consideration_words: collapseSelfRepeat(norm(s.considerationWords)),
    consideration_figures: norm(s.considerationFigures),
    // 양도인
    transferor_name: transferorName,
    transferor_details: norm(s.transferorDetails) || transferorName,
    transferor_id: norm(s.transferorId),
    // 양수인
    transferee_name: transfereeName,
    transferee_details: formatNameAddressForDocument(s.transfereeDetails || '', transfereeName),
    transferee_person: norm(s.transfereePerson),
    transferee_corporate: norm(s.transfereeCorporate),
    transferee_is_individual: kind === 'individual',
    transferee_is_corporate: kind === 'corporate',
    // 인종 체크박스 (개인)
    r_my: mark(kind === 'individual' && race === 'malay'),
    r_ch: mark(kind === 'individual' && race === 'chinese'),
    r_in: mark(kind === 'individual' && race === 'indian'),
    r_ot: mark(kind === 'individual' && race === 'others'),
    // 법인유형 체크박스 (법인)
    b_gov: mark(kind === 'corporate' && corpType === 'gov'),
    b_mn: mark(kind === 'corporate' && corpType === 'mn'),
    b_mnn: mark(kind === 'corporate' && corpType === 'mnn'),
    b_nm: mark(kind === 'corporate' && corpType === 'nm'),
    // 증인 4명
    ...witnessVars,
  };
}
